import { mapLookup, displayErrors } from "common/helpers";
import {
    scenarioSvc as service,
    lookupSvc,
    taskSvc,
    dashboardsService as dshbSvc,
    dashboardGroupsService as dshbGrSvc,
    snowflakeLoadService,
    snowflakeLoadConfigService
} from "services/serviceStorage";
import {
    FetchScenarioEditLookupsParams,
    FetchScenariosParams,
    ScenarioInputFile,
    UploadInputParams,
    DownloadInputsParams,
    RunExternalFunctionsParams,
    FetchLinksParams,
    Link,
    ScenarioEditLookups,
    ReportTemplate,
    ExternalFunction,
    SaveScenarioPayload
} from "./types";
import { BaseApiResponse, Scenario, Release } from "common/types";
import { wrapRequest } from "common/utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { Dashboard, DashboardGroup } from "store/dashboardViewer/types";
import { LoadConfig } from "store/staticReportsSnowflake/types";

export const fetchScenarioEditLookups = createAsyncThunk<ScenarioEditLookups, FetchScenarioEditLookupsParams>(
    "scenarioMgmt/fetchScenarioEditLookups",
    async ({ taskId, releaseGroupId }, { rejectWithValue }) => {
        try {
            const requests = [
                lookupSvc.getPartitioningModes(),
                lookupSvc.getTimeUnits(),
                lookupSvc.getReleases(taskId),
                lookupSvc.getCubeTemplates(taskId),
                lookupSvc.getReportTemplates(taskId),
                lookupSvc.getVMGroups(),
                taskSvc.getTask(taskId),
                dshbSvc.getDashboardsList({ releaseGroupIds: [releaseGroupId] }),
                dshbGrSvc.getDashboardGroupsList({ releaseGroupIds: [releaseGroupId] }),
                snowflakeLoadConfigService.list({ releaseGroupIds: [releaseGroupId] })
            ];

            if (taskId) {
                requests.push(
                    taskSvc.getScenarioGroupsLookup(taskId),
                    snowflakeLoadConfigService.list({ taskIds: [taskId], includeGlobal: true, releaseGroupIds: [releaseGroupId] })
                );
            }

            const [
                partitioningRes,
                timeUnitRes,
                releasesRes,
                cubeTemplatesRes,
                reportTemplatesRes,
                vmGroupsRes,
                taskRes,
                dashboardsRes,
                dashboardGroupsRes,
                taskConfigRes,
                groupsRes,
                configsRes
            ] = await Promise.all(requests);

            return {
                partitioning: partitioningRes.data.result.map(mapLookup),
                timeUnits: timeUnitRes.data.result.map(mapLookup),
                releases: releasesRes.data.result.map((r: Release) => ({
                    ...r,
                    key: r.id,
                    displayName: r.displayName,
                    releaseGroupId: r.releaseGroupId,
                })),
                cubeTemplates: cubeTemplatesRes.data.result.map(mapLookup),
                reportTemplates: reportTemplatesRes.data.result.map((r: ReportTemplate) => ({
                    key: r.id,
                    displayName: r.displayName,
                    releaseGroupId: r.releaseGroupId,
                    isGlobal: r.isGlobal,
                })),
                vmGroups: vmGroupsRes.data.result.map(mapLookup),
                task: taskRes.data.result,
                dashboards: dashboardsRes?.data.result.items.map((d: Dashboard) => ({
                    value: d.dashboardId,
                    label: d.dashboardName,
                })) ?? [],
                dashboardGroups: dashboardGroupsRes?.data.result.items.map((g: DashboardGroup) => ({
                    value: g.groupId,
                    label: g.groupName,
                    isGroup: true,
                    source: g.dashboards.map(d => d.id),
                })) ?? [],
                taskLoadConfigs: taskConfigRes?.data.result.items ?? [],
                groups: groupsRes?.data.result.map(mapLookup) ?? [],
                loadConfigs: configsRes?.data.result.items.map((c: LoadConfig) => ({
                    value: c.id,
                    label: c.name,
                })) ?? []
            }
        } catch (error) {
            displayErrors(["Internal server error. Please, contact system administrator."]);
            return rejectWithValue(error);
        }
    }
);

export const fetchScenarios = createAsyncThunk<{ items: Scenario[]; totalCount: number; }, FetchScenariosParams>(
    'scenarioMgmt/fetchScenarios',
    async (params) => {
        console.trace();
        console.log('fetchScenarios filter: ', params.filter);
        const { data } = await wrapRequest<BaseApiResponse<{ items: Scenario[]; totalCount: number; }>>({
            handler: () => service.getScenarios(params.filter),
            showNotifications: true
        });

        return data!.result;
    }
);

export const fetchExternalFunctions = createAsyncThunk<ExternalFunction[], number>(
    'scenarioMgmt/fetchExternalFunctions',
    async (taskId) => {
        const { data } = await wrapRequest<BaseApiResponse<ExternalFunction[]>>({
            handler: () => service.getExternalFunctions(taskId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const deleteScenarios = createAsyncThunk<void, number[]>(
    'scenarioMgmt/deleteScenarios',
    async (scenarioIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.deleteScenarios(scenarioIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const fetchScenario = createAsyncThunk<Scenario, number>(
    'scenarioMgmt/fetchScenario',
    async (scenarioId) => {
        const { data } = await wrapRequest<BaseApiResponse<Scenario>>({
            handler: () => service.getScenario(scenarioId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const saveScenario = createAsyncThunk<number, SaveScenarioPayload>(
    'scenarioMgmt/saveScenario',
    async (scenario, { rejectWithValue }) => {
        const { data, errors, warnings, infos } = await wrapRequest<BaseApiResponse<number>>({
            handler: () => service.putScenario(scenario),
            showNotifications: true
        });

        if (errors?.length || warnings?.length) {
            return rejectWithValue({ errors, warnings, infos });
        }

        return data!.result;
    }
);

export const copyInputs = createAsyncThunk<void, { sourceId: number; destinationId: number; }>(
    'scenarioMgmt/copyInputs',
    async ({ sourceId, destinationId }, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.copyInputs({ sourceId, destinationId }),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const validateScenarios = createAsyncThunk<void, number[]>(
    'scenarioMgmt/validateScenarios',
    async (scenarioIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.validateScenarios(scenarioIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const runScenarios = createAsyncThunk<void, number[]>(
    'scenarioMgmt/runScenarios',
    async (scenarioIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.runScenarios(scenarioIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const stopScenarios = createAsyncThunk<void, number[]>(
    'scenarioMgmt/stopScenarios',
    async (scenarioIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.stopScenarios(scenarioIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const checkInstanceCapacity = createAsyncThunk<{ maxInstances: number; instanceCount: number }, number[]>(
    'scenarioMgmt/checkInstanceCapacity',
    async (scenarioIds) => {
        const { data } = await wrapRequest<BaseApiResponse<{ maxInstances: number; instanceCount: number }>>({
            handler: () => service.checkInstanceCapacity(scenarioIds),
            showNotifications: true
        });

        return data!.result;
    }
);

export const fetchInputFiles = createAsyncThunk<ScenarioInputFile[], number>(
    'scenarioMgmt/fetchInputFiles',
    async (scenarioId) => {
        const { data } = await wrapRequest<BaseApiResponse<ScenarioInputFile[]>>({
            handler: () => service.getInputs(scenarioId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const uploadInputs = createAsyncThunk<void, UploadInputParams>(
    'scenarioMgmt/uploadInputs',
    async ({ scenarioId, files }, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.uploadInputs({ scenarioId, files }),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const deleteInputs = createAsyncThunk<void, DownloadInputsParams>(
    'scenarioMgmt/deleteInputs',
    async ({ scenarioId, files }, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.deleteInputs({ scenarioId, files }),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const downloadInputs = createAsyncThunk<string[], DownloadInputsParams>(
    'scenarioMgmt/downloadInputs',
    async ({ scenarioId, files }) => {
        const { data } = await wrapRequest<BaseApiResponse<string[]>>({
            handler: () => service.downloadInputs({ scenarioId, files }),
            showNotifications: true
        });

        return data!.result;
    }
);

export const loadResults = createAsyncThunk<void, number[]>(
    'scenarioMgmt/loadResults',
    async (scenarioIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.loadResults(scenarioIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const runForceMerge = createAsyncThunk<void, number[]>(
    'scenarioMgmt/runForceMerge',
    async (scenarioIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.runForceMerge(scenarioIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const restartFailedCases = createAsyncThunk<void, number[]>(
    'scenarioMgmt/restartFailedCases',
    async (scenarioIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.restartFailedCases(scenarioIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const runExternalFunctions = createAsyncThunk<void, RunExternalFunctionsParams>(
    'scenarioMgmt/runExternalFunctions',
    async ({ request }, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.runExternalFunctions(request),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const fetchLinks = createAsyncThunk<{ items: Link[]; totalCount: number; }, FetchLinksParams>(
    'scenarioMgmt/fetchLinks',
    async ({ filter }) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: Link[]; totalCount: number; }>>({
            handler: () => service.getLinks(filter),
            showNotifications: true
        });

        return data!.result;
    }
);

export const loadAllTablesSnowflake = createAsyncThunk<void, number[]>(
    'scenarioMgmt/loadAllTablesSnowflake',
    async (scenarioIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => snowflakeLoadService.loadAllTables(scenarioIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const unloadAllTablesSnowflake = createAsyncThunk<void, number[]>(
    'scenarioMgmt/unloadAllTablesSnowflake',
    async (scenarioIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => snowflakeLoadService.unloadAllTables(scenarioIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const loadScenariosConfigsSnowflake = createAsyncThunk<void, number[]>(
    'scenarioMgmt/loadScenariosConfigsSnowflake',
    async (scenarioIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => snowflakeLoadService.loadScenariosConfigs(scenarioIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);
